/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* Ajuste de la animacion de la galeria de producto solo en Android. */
(function () {
  if (!/Android/i.test(navigator.userAgent || '')) {
    return;
  }

  document.addEventListener('flickity:ready', function (event) {
    var carousel = event.target;

    if (!carousel || !carousel.matches || !carousel.matches('flickity-carousel.product__media-list')) {
      return;
    }

    if (!carousel.closest('product-media[data-lv-android-gallery]')) {
      return;
    }

    Promise.resolve(carousel.flickityInstance).then(function (instance) {
      if (!instance || !instance.options || instance.__lvAndroidGallerySwipeFixV4) {
        return;
      }

      instance.__lvAndroidGallerySwipeFixV4 = true;

      var swipeStartIndex = null;
      var dragMinX = null;
      var dragMaxX = null;
      var lastPointerX = null;
      var lastMoveTime = 0;
      var swipeSpeed = 0;
      var settleAnimation = null;

      var originalDragEnd = instance.dragEnd;
      var originalSelect = instance.select;

      function getRenderedX() {
        var transform = window.getComputedStyle(instance.slider).transform;

        if (!transform || transform === 'none') {
          return instance.x;
        }

        var translateX = 0;

        if (window.DOMMatrixReadOnly) {
          translateX = new window.DOMMatrixReadOnly(transform).m41;
        } else {
          var values = transform.match(/matrix(?:3d)?\(([^)]+)\)/);

          if (!values) {
            return instance.x;
          }

          var parts = values[1].split(',').map(function (value) {
            return parseFloat(value.trim());
          });

          translateX = parts.length === 16 ? parts[12] : parts[4];
        }

        if (instance.options.rightToLeft) {
          translateX *= -1;
        }

        return translateX - instance.cursorPosition;
      }

      function cancelSettleAndSync() {
        if (!settleAnimation) {
          return;
        }

        var renderedX = getRenderedX();

        settleAnimation.cancel();
        settleAnimation = null;

        instance.x = renderedX;
        instance.dragX = renderedX;
        instance.velocity = 0;
        instance.setTranslateX(renderedX, true);
      }

      function animateToIndex(index, isWrap, duration) {
        cancelSettleAndSync();

        var fromIndex = instance.selectedIndex;
        var fromX = instance.x;
        var fromTransform = instance.slider.style.transform || window.getComputedStyle(instance.slider).transform;

        originalSelect.call(instance, index, isWrap, true);

        var targetIndex = instance.selectedIndex;
        var targetX = instance.x;
        var toTransform = instance.slider.style.transform;
        var distance = Math.abs(targetX - fromX);

        if (!duration) {
          var indexDistance = Math.abs(targetIndex - fromIndex);

          duration = Math.min(
            300,
            240 + Math.max(0, indexDistance - 1) * 10
          );
        }

        if (
          !instance.slider.animate ||
          !fromTransform ||
          fromTransform === 'none' ||
          fromTransform === toTransform ||
          distance <= 0.5
        ) {
          instance.slider.style.willChange = 'auto';
          instance.dispatchEvent('settle', null, [targetIndex]);
          return;
        }

        instance.slider.style.willChange = 'transform';

        settleAnimation = instance.slider.animate(
          [
            { transform: fromTransform },
            { transform: toTransform }
          ],
          {
            duration: duration,
            easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)'
          }
        );

        settleAnimation.onfinish = function () {
          settleAnimation = null;
          instance.slider.style.willChange = 'auto';
          instance.dispatchEvent('settle', null, [targetIndex]);
        };
      }

      /*
       * Los cambios automaticos:
       * - colores
       * - puntos
       * - anterior/siguiente
       *
       * usan la misma animacion de compositor que el swipe.
       */
      instance.select = function (index, isWrap, isInstant) {
        if (isInstant) {
          cancelSettleAndSync();

          var result = originalSelect.call(
            this,
            index,
            isWrap,
            true
          );

          this.slider.style.willChange = 'auto';

          return result;
        }

        animateToIndex(index, isWrap);
      };

      instance.on('pointerDown', function () {
        cancelSettleAndSync();
        instance.slider.style.willChange = 'transform';
      });

      instance.on('dragStart', function (event, pointer) {
        swipeStartIndex = instance.selectedIndex;

        var previousIndex = Math.max(
          0,
          swipeStartIndex - 1
        );

        var nextIndex = Math.min(
          instance.slides.length - 1,
          swipeStartIndex + 1
        );

        var previousX =
          -instance.slides[previousIndex].target;

        var nextX =
          -instance.slides[nextIndex].target;

        dragMinX = Math.min(
          previousX,
          nextX
        );

        dragMaxX = Math.max(
          previousX,
          nextX
        );

        lastPointerX =
          pointer &&
          typeof pointer.pageX === 'number'
            ? pointer.pageX
            : null;

        lastMoveTime = performance.now();
        swipeSpeed = 0;

        instance.isAnimating = false;
        instance.velocity = 0;
      });

      instance.on('dragMove', function (event, pointer) {
        if (
          swipeStartIndex === null ||
          dragMinX === null ||
          dragMaxX === null ||
          typeof instance.dragX !== 'number'
        ) {
          return;
        }

        var now = performance.now();

        if (
          pointer &&
          typeof pointer.pageX === 'number' &&
          lastPointerX !== null
        ) {
          var elapsed =
            now - lastMoveTime;

          if (
            elapsed > 0 &&
            elapsed < 100
          ) {
            var currentSpeed =
              Math.abs(
                pointer.pageX -
                lastPointerX
              ) / elapsed;

            swipeSpeed = Math.max(
              currentSpeed,
              swipeSpeed * 0.72
            );
          }

          lastPointerX =
            pointer.pageX;

          lastMoveTime = now;
        }

        instance.dragX = Math.max(
          dragMinX,
          Math.min(
            dragMaxX,
            instance.dragX
          )
        );

        instance.x =
          instance.dragX;

        instance.velocity = 0;

        instance.setTranslateX(
          instance.x,
          true
        );
      });

      instance.dragEnd = function (event, pointer) {
        if (
          !this.isDraggable ||
          swipeStartIndex === null ||
          typeof this.dragX !== 'number' ||
          typeof this.dragStartPosition !== 'number' ||
          !this.slides.length
        ) {
          return originalDragEnd.call(
            this,
            event,
            pointer
          );
        }

        var dragDistance =
          this.dragX -
          this.dragStartPosition;

        var direction = 0;

        if (dragDistance < 0) {
          direction = 1;
        } else if (dragDistance > 0) {
          direction = -1;
        }

        if (this.options.rightToLeft) {
          direction *= -1;
        }

        var targetIndex = Math.max(
          0,
          Math.min(
            this.slides.length - 1,
            swipeStartIndex + direction
          )
        );

        var targetX =
          -this.slides[targetIndex].target;

        var remainingDistance =
          Math.abs(
            targetX - this.x
          );

        var distanceRatio =
          Math.min(
            1,
            remainingDistance /
              Math.max(
                1,
                this.size.innerWidth
              )
          );

        var duration;

        if (swipeSpeed >= 1.2) {
          duration = 155;
        } else if (swipeSpeed >= 0.75) {
          duration = 175;
        } else if (swipeSpeed >= 0.4) {
          duration = 195;
        } else {
          duration = 220;
        }

        duration = Math.round(
          duration *
            (
              0.55 +
              0.45 * distanceRatio
            )
        );

        duration = Math.max(
          140,
          Math.min(
            220,
            duration
          )
        );

        this.isAnimating = false;
        this.velocity = 0;

        delete this.previousDragX;
        delete this.isFreeScrolling;

        animateToIndex(
          targetIndex,
          false,
          duration
        );

        this.dispatchEvent(
          'dragEnd',
          event,
          [pointer]
        );

        swipeStartIndex = null;
        dragMinX = null;
        dragMaxX = null;
        lastPointerX = null;
        lastMoveTime = 0;
        swipeSpeed = 0;
      };
    });
  });
})();